/**
 * Download config for the Moly Super Agent desktop app.
 *
 * Installers live on the public github.com/pheyhui/moly-super-agent releases. Asset
 * filenames embed the version, so there is NO static "latest asset" URL — instead we
 * resolve the newest build at download time via the GitHub latest-release API (public,
 * no auth) and hand the browser the asset's `browser_download_url`. If that call fails
 * (offline, rate-limited), the UI falls back to opening the releases page.
 */
export const SUPER_AGENT_DESKTOP = {
  repo: 'pheyhui/moly-super-agent',
  // Public GitHub API — returns the latest release + its assets.
  latestReleaseApi: 'https://api.github.com/repos/pheyhui/moly-super-agent/releases/latest',
  // Human-facing fallback if the API resolve fails.
  releasesPage: 'https://github.com/pheyhui/moly-super-agent/releases/latest',
  // Matches the macOS Apple Silicon installer asset (e.g. ...-darwin-arm64-0.1.1.zip),
  // excluding the .blockmap sidecar.
  macArm64AssetPattern: /darwin-arm64-.*\.zip$/,
} as const

export type DesktopOs = 'mac-arm64' | 'mac-intel' | 'windows' | 'linux'

// OS picker options. Only macOS Apple Silicon has a build today; the rest are shown
// disabled so the roadmap is visible without implying they work yet.
export const DESKTOP_OS_OPTIONS: Array<{
  value: DesktopOs
  label: string
  available: boolean
}> = [
  { value: 'mac-arm64', label: 'macOS (Apple Silicon)', available: true },
  { value: 'mac-intel', label: 'macOS (Intel)', available: false },
  { value: 'windows', label: 'Windows', available: false },
  { value: 'linux', label: 'Linux', available: false },
]

type GitHubAsset = { name: string; browser_download_url: string }
type GitHubRelease = { tag_name: string; assets: Array<GitHubAsset> }

/**
 * Resolve the direct download URL for the latest macOS Apple Silicon build.
 * Raw `fetch` (not AuthAxios) on purpose: this is the public GitHub API on a different
 * host — it must not carry Moly auth headers. Throws if the release or asset is missing;
 * the caller falls back to the releases page.
 */
export async function fetchLatestMacDownload(): Promise<{ url: string; version: string }> {
  const res = await fetch(SUPER_AGENT_DESKTOP.latestReleaseApi, {
    headers: { Accept: 'application/vnd.github+json' },
  })
  if (!res.ok) throw new Error(`GitHub API responded ${res.status}`)
  const release = (await res.json()) as GitHubRelease
  const asset = release.assets?.find((a) => SUPER_AGENT_DESKTOP.macArm64AssetPattern.test(a.name))
  if (!asset) throw new Error('No macOS Apple Silicon asset in the latest release')
  return { url: asset.browser_download_url, version: release.tag_name }
}
